import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { ProfileContext } from '../../../context/Profile.context';
import { errors, corporateEntityObj } from '../../../constants';
import { checkBeneficialOwners, isBeneficialOwnerComplete, isCorporateEntityComplete } from '../../../utils/completes';
import { isBoolean } from '../../../utils/validations';
import { createID, personEntityTitle } from '../../../utils/helper';
import Controls from '../../Controls';
import FieldError from '../../FieldError';
import Title from '../../Title';
import QuestionCard from '../../QuestionCard';
import RemovePerson from '../../RemovePerson';

import { Lock, User, UserCheck, UserLock, UserPlus, Trash2, Check } from 'lucide-react';
import { Box, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Dialog, DialogContent, DialogTitle, EntityLabelGroup, EntityLabel, Label, TextField, Typography, InfoPanel, SectionTitle, Divider } from '@otcmarketsgroup/otcm-ui-lib';

const OWNER = 'owner';
const CORP_ENTITY_VIEW = 'addCorpEntity';
const ADD_NEW_PARTICIPANTS_VIEW = 'addNewOwner';
const REMOVE_PERSON_VIEW = 'removePerson';
const DRAFT_CORP_ENTITY_KEY = '__draft_corp_entity__';

const CorporateEntities = ({ noControlPerson, onControlPersonListUpdate, onControlPersonRemove }) => {
  const [profileState] = useContext(ProfileContext);
  const [person, setPerson] = useState(corporateEntityObj);
  const [dialog, setDialog] = useState(false);
  const [slideView, setSlideView] = useState(CORP_ENTITY_VIEW);
  const [showInlineOwnerForm, setShowInlineOwnerForm] = useState(false);
  const [editingOwnerId, setEditingOwnerId] = useState(null);
  const [corporateEntityView, setCorporateEntityView] = useState(corporateEntityObj);
  const [individualOwnerFocus, setIndividualOwnerFocus] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [controlPersons, setStaticControlPersons] = useState(JSON.parse(JSON.stringify(profileState.profile.controlPersons)));
  const staticControlPersonList = profileState.profile.controlPersons;
  const [controlPersonsList, setControlPersonsList] = useState(controlPersons || []);
  const getEntityKey = (entity = {}) => entity.corpEntity || DRAFT_CORP_ENTITY_KEY;
  const corporateEntityList = staticControlPersonList && staticControlPersonList.filter((person, index) => (person.corpEntity && !person.hasBeneficialOwners && !person.isDeleted)
    || (person.corpEntity && person.hasBeneficialOwners && staticControlPersonList.findIndex((item) => item.corpEntity === person.corpEntity && !item.isDeleted) === index));
  const corporateEntityViewTitle = corporateEntityView && corporateEntityView.corpEntity || '';
  const owners = controlPersonsList.filter(obj => !obj.isDeleted && obj.firstName && obj.lastName && obj.corpEntity === getEntityKey(corporateEntityView));
  const hasOwners = owners && owners.length > 0;
  const hasFirstName = !!(person && person.firstName && person.firstName.trim().length > 0);
  const hasLastName = !!(person && person.lastName && person.lastName.trim().length > 0);
  const isOwnerAddDisabled = !hasFirstName || !hasLastName;
  const addTitle = 'Add Corporate Entity (Control Person)';
  const isAdd = !corporateEntityView.id;
  const isRemoveView = slideView === REMOVE_PERSON_VIEW;
  const isReadOnly = profileState.readOnly;
  const renderIndividualOwner = (onClick, showError) => {
    const list = [...owners];
    const isMaxOwners = list && list.length >= 3;

    const onRemove = (id) => {
      const updateControlPersonList = [...controlPersonsList];
      const personUpdate = updateControlPersonList.find(item => item.id && item.id === id);
      personUpdate.isDeleted = true;

      setControlPersonsList(updateControlPersonList);
    };

    const onEdit = (id) => {
      const person = controlPersonsList.find(person => person.id === id);
      if (!person) return;

      setPerson(person);
      setValidationError(false);
      setShowInlineOwnerForm(false);
      setEditingOwnerId(id);
    };

    return (
      <div className='mtMed'
        onMouseEnter={() => setIndividualOwnerFocus(true)}
        onMouseLeave={() => setIndividualOwnerFocus(false)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
            {(showError && (!list || list.length === 0)) && <FieldError error={errors.INCOMPLETE_DATA_FIELD} isFocus={individualOwnerFocus} />}
            <Title title='Individual Owner' type='h3' />
          </Box>
          {(!isMaxOwners && !isReadOnly) && <Button variant='text' onClick={onClick}>
            Add Beneficial Owner
          </Button>}
        </Box>
        {!hasOwners && <Typography variant='body2'>
          No Beneficial Owner has been added for this corporate entity.
        </Typography>}
        {hasOwners && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {owners.map(person => {
            const isComplete = isBeneficialOwnerComplete(person);
            const name = personEntityTitle(person, false);
            const id = person.id;
            if (person) {
              if (editingOwnerId === id && !isReadOnly) {
                return <Card key={id} padding={2} hasBorder type="highlight">
                  <CardHeader>
                    <CardTitle icon={<UserCheck />} title={name} type='h3' />
                  </CardHeader>
                  {renderOwnerForm()}
                  {validationError && <Label isError title={errors.INCOMPLETE_DATA} />}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                    <Button variant='text' onClick={handleCancelOwnerView}>
                      Cancel
                    </Button>
                    <Button variant='text' onClick={handleOwnerUpdate} disabled={isOwnerAddDisabled}>
                      Update
                    </Button>
                  </Box>
                </Card>;
              }

              return <EntityLabel
                key={id}
                title={name}
                subtitle={isReadOnly ? '' : isComplete ? 'Complete' : 'Enter Information'}
                icon={isComplete ? <UserCheck /> : <User />}
                actionIcon={!isReadOnly ? <Trash2 /> : undefined}
                isComplete={isComplete}
                isWarning={!isComplete}
                onEntityClick={() => onEdit(id)}
                onActionClick={!isReadOnly ? () => onRemove(id) : undefined} />;
            }
          })}
          {isMaxOwners && <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
            <Check size={12} color="var(--otc-medium-gray)" />
            <Typography variant='body2'>
              Maximum number of beneficial owners reached.
            </Typography>
          </Box>}
        </Box>}
        {((!list || list.length === 0) && isReadOnly) && <Entity
          title='No Beneficial Owner'
          isEmpty
          size='small' />}
      </div>
    );
  };

  const question = {
    title: 'Control/Ownership',
    subTitle: 'Is there an individual who beneficially owns or controls this entity?',
    fieldName: 'hasBeneficialOwners',
    falseDetails: [
      {
        desc: 'Please explain why an individual cannot be identified under this corporate entity.',
        fieldName: 'reasonForNone'
      }
    ],
    trueDetails: {
      component: () => <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {renderIndividualOwner(() => {
          setPerson(corporateEntityObj);
          setValidationError(false);
          setEditingOwnerId(null);
          setShowInlineOwnerForm(true);
        }, validationError)}
        {showInlineOwnerForm && !isReadOnly && <Card hasBorder type="highlight">
          <CardHeader>
            <CardTitle icon={<UserPlus />} title='Add Beneficial Owner' type='h3' />
          </CardHeader>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant='body1' >
                Enter Full Legal Name of Beneficial Owner.
              </Typography>
              {renderOwnerForm()}
              {validationError && <Label isError title={errors.INCOMPLETE_DATA} />}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant='text' onClick={handleCancelOwnerView}>
                  Cancel
                </Button>
                <Button variant='text' onClick={handleOwnerAdd} disabled={isOwnerAddDisabled}>
                  Add
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>}
      </Box>
    }
  };

  useEffect(() => {
    setValidationError(false);
  }, [slideView]);


  useEffect(() => {
    const newList = JSON.parse(JSON.stringify(profileState.profile.controlPersons));
    setStaticControlPersons(newList);
    setControlPersonsList(newList);
  }, [profileState.profile.controlPersons]);

  const onIndividualValueChanged = (type, value, field) => {
    if (type === OWNER) {
      setPerson(prevState => {
        return {
          ...prevState,
          [field]: value
        };
      });
    }
  };

  const onValueChanged = (value, field) => {
    const updatedEntity = corporateEntityView;
    updatedEntity[field] = value;

    if (field === 'hasBeneficialOwners' && value === true) updatedEntity.reasonForNone = null;
    if (field === 'hasBeneficialOwners' && value === false) {
      updatedEntity.owners = [];
      setShowInlineOwnerForm(false);
      setEditingOwnerId(null);
      setPerson(corporateEntityObj);
    }

    setCorporateEntityView({ ...updatedEntity });
  };

  const onCorpEntityNameChanged = e => {
    const value = e.target.value;
    setCorporateEntityView({
      ...corporateEntityView,
      newCorpEntity: value
    });
  };

  const handleOwnerAdd = () => {
    let newPerson = person;
    const entityKey = getEntityKey(corporateEntityView);

    setValidationError(false);

    if (!checkBeneficialOwners(newPerson)) {
      setValidationError(true);
      return;
    }

    if (!person.id) {
      const id = createID(controlPersonsList);
      newPerson.id = id;
    }

    newPerson = {
      ...corporateEntityView,
      corpEntity: entityKey,
      id: person.id,
      isCorpEntity: true,
      firstName: person.firstName,
      middleName: person.middleName,
      lastName: person.lastName
    };

    setControlPersonsList([...controlPersonsList, newPerson]);
    setPerson(corporateEntityObj);
    setShowInlineOwnerForm(false);
    setEditingOwnerId(null);
    setSlideView(CORP_ENTITY_VIEW);
  };

  const handleOwnerUpdate = () => {
    setValidationError(false);

    if (!checkBeneficialOwners(person)) {
      setValidationError(true);
      return;
    }

    let updatePeopleList = [...controlPersonsList];

    updatePeopleList = updatePeopleList.map(item => {
      if (item.id && (item.id === person.id)) return person;

      return item;
    });

    setControlPersonsList(updatePeopleList);
    setPerson(corporateEntityObj);
    setShowInlineOwnerForm(false);
    setEditingOwnerId(null);
    setSlideView(CORP_ENTITY_VIEW);
  };

  const handleEdit = item => {
    setCorporateEntityView({ ...item, newCorpEntity: item.corpEntity });
    setDialog(true);
  };

  const handleCancelView = () => {
    setDialog(false);
  };

  const handleCancelOwnerView = () => {
    setPerson(corporateEntityObj);
    setShowInlineOwnerForm(false);
    setEditingOwnerId(null);
    setSlideView(CORP_ENTITY_VIEW);
  };

  const handleSubmit = () => {
    if (corporateEntityView.hasBeneficialOwners) handleSubmitHasOwners();
    if (!corporateEntityView.hasBeneficialOwners) handleSubmitNoOwners();
  };

  const handleRemoveClick = (person) => {
    setCorporateEntityView(person);
    setSlideView(REMOVE_PERSON_VIEW);
    setDialog(true);
  };

  const handleSubmitNoOwners = () => {
    setValidationError(false);

    const corporateEntity = { ...corporateEntityView };
    let listUpdate = controlPersonsList;

    if (!isCorporateEntityComplete(corporateEntity)) {
      setValidationError(true);
      return;
    }

    listUpdate = controlPersonsList.map(item => {
      if (item.id === corporateEntity.id) item.isDeleted = true;
      return item;
    });

    if (!corporateEntity.id || controlPersonsList.find(item => item.id === corporateEntity.id)) {
      const id = createID(controlPersonsList);
      corporateEntity.id = id;
    }

    corporateEntity.corpEntity = corporateEntity.newCorpEntity;
    corporateEntity.isCorpEntity = true;
    corporateEntity.firstName = null;
    corporateEntity.middleName = null;
    corporateEntity.lastName = null;

    listUpdate = [...listUpdate, corporateEntity];

    onControlPersonListUpdate(listUpdate);
    setDialog(false);
  };

  const handleSubmitHasOwners = () => {
    setValidationError(false);
    const entityKey = getEntityKey(corporateEntityView);

    let listUpdate = controlPersonsList;

    // CHECK VALIDATION
    if (!owners || owners.length === 0) {
      setValidationError(true);
      return;
    }

    listUpdate = controlPersonsList.filter(item => {
      if (item.hasBeneficialOwners && item.corpEntity === entityKey) {
        item.hasBeneficialOwners = true;
        item.corpEntity = corporateEntityView.newCorpEntity;
        item.reasonForNone = null;

        return item;
      } else if (!item.hasBeneficialOwners && item.corpEntity === entityKey) {
        item.isDeleted = true;
      }

      return item;
    });

    onControlPersonListUpdate(listUpdate);
    setDialog(false);
  };

  const handleRemove = (entity) => {
    onControlPersonRemove(entity);
    dialogClose();
  };

  const dialogClose = () => {
    setDialog(false);
  };

  const handleDialogClose = () => {
    setControlPersonsList(controlPersons);
    setSlideView(CORP_ENTITY_VIEW);
    setValidationError(false);
    setPerson(corporateEntityObj);
    setShowInlineOwnerForm(false);
    setEditingOwnerId(null);
    setCorporateEntityView({corporateEntityObj});
  };

  const renderCorporateEntity = () => <>
    <DialogContent>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Typography variant='body1'>
          Enter the name of the corporate entity that owns 10% or more of the issuer's shares.
        </Typography>
        <TextField
          label="Name"
          placeholder="Name"
          showClearButton
          maxLength={75}
          value={corporateEntityView && corporateEntityView.newCorpEntity}
          readOnly={isReadOnly}
          error={validationError && !corporateEntityView?.newCorpEntity}
          helperText={validationError && !corporateEntityView?.newCorpEntity ? errors.INCOMPLETE_DATA_FIELD : ""}
          onChange={onCorpEntityNameChanged} />
        <QuestionCard
          item={question}
          section={corporateEntityView}
          readOnly={isReadOnly}
          showError={validationError}
          isValid={validationError ? isBoolean(corporateEntityView[question.fieldName]) : true}
          handleQuestionClick={(val, field) => onValueChanged(val, field)}
          onValueChanged={(val, field) => onValueChanged(val, field)} />
        {validationError && !showInlineOwnerForm && !editingOwnerId && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
      </Box>
    </DialogContent>
    {
      !isReadOnly && <Controls
        className='mtXXL'
        cancelText='Cancel'
        submitText={isAdd ? 'Add' : 'Update'}
        onCancelClick={handleCancelView}
        onSubmitClick={handleSubmit}
      />
    }
  </>;

  const renderNewOwner = () => <><DialogContent>
    <Title title='Add Beneficial Owner' type='h2' />
    {renderOwnerForm()}
    {validationError && <Label className='mtLg' isError title={errors.INCOMPLETE_DATA} />}
  </DialogContent>
    <Controls
      cancelText='Cancel'
      submitText='Add'
      isSubmitDisabled={isOwnerAddDisabled}
      onCancelClick={handleCancelOwnerView}
      onSubmitClick={handleOwnerAdd} />;
  </>;

  const renderOwnerForm = () => <>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
      <Box>
        <TextField
          label='First Name'
          placeholder='First Name'
          showClearButton
          value={person?.firstName || ''}
          readOnly={isReadOnly}
          maxLength={75}
          disabled={isReadOnly || person && person.isPreExisting}
          onChange={e => onIndividualValueChanged(OWNER, e.target.value, 'firstName')} />
      </Box>
      <Box>
        <TextField
          label='Middle Name'
          placeholder='Middle Name'
          showClearButton
          maxLength={75}
          value={person?.middleName || ''}
          readOnly={isReadOnly}
          disabled={isReadOnly || person && person.isPreExisting}
          onChange={e => onIndividualValueChanged(OWNER, e.target.value, 'middleName')} />
      </Box>
      <Box sx={{ gridColumn: '1 / -1' }}>
        <TextField
          label='Last Name'
          placeholder='Last Name'
          showClearButton
          maxLength={75}
          value={person?.lastName || ''}
          readOnly={isReadOnly}
          disabled={isReadOnly || person && person.isPreExisting}
          onChange={e => onIndividualValueChanged(OWNER, e.target.value, 'lastName')} />
      </Box>
    </Box>
  </>;

  return (
    <div>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <SectionTitle noUnderline>
          Corporate Entities
        </SectionTitle>
        {(!isReadOnly && !noControlPerson) && <Button variant='text' onClick={() => setDialog(true)}>
          Add Corporate Entity (Control Person)
        </Button>}
      </Box>
      {!noControlPerson && <>
        {(!corporateEntityList || corporateEntityList.length === 0) && <Typography variant='body2'>
          No Corporate Entities have been added yet.
        </Typography>}
        <EntityLabelGroup>
          {corporateEntityList.map((item, i) => {
            const isComplete = isCorporateEntityComplete(item);

            return (
              <EntityLabel
                key={i}
                title={item.corpEntity}
                subtitle={isComplete ? 'Complete' : 'Enter Information'}
                icon={isComplete ? <UserCheck /> : <User />}
                actionIcon={!isReadOnly ? <Trash2 /> : undefined}
                isComplete={isComplete}
                isWarning={!isComplete}
                onEntityClick={() => handleEdit(item)}
                onActionClick={!isReadOnly ? () => handleRemoveClick(item) : undefined} />
            );
          })}
        </EntityLabelGroup>
      </>}
      {noControlPerson && <EntityLabel
        title='Not Available'
        subtitle='Complete'
        icon={<UserCheck />}
        isComplete />}
      <Dialog open={dialog} onClose={dialogClose} onTransitionExited={handleDialogClose}>
        <DialogTitle title={isAdd ? addTitle : corporateEntityViewTitle} onClose={dialogClose} />
        {slideView === CORP_ENTITY_VIEW && renderCorporateEntity()}
        {slideView === ADD_NEW_PARTICIPANTS_VIEW && renderNewOwner()}
        {isRemoveView && <RemovePerson
          person={{ ...corporateEntityView }}
          // selectedRemove={isIndividual}
          onCancel={dialogClose}
          onRemoveSubmit={handleRemove} />}
      </Dialog>
    </div>
  );
};

CorporateEntities.propTypes = {
  noControlPerson: PropTypes.bool,
  onControlPersonAdd: PropTypes.func,
  onControlPersonUpdate: PropTypes.func,
  onControlPersonListUpdate: PropTypes.func,
  onControlPersonRemove: PropTypes.func
};

export default CorporateEntities;
